import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect } from 'react';
import { Form } from '@radix-ui/react-form';
import type { SubmitHandler } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@querodelivery/uni-react';
import { colors } from '@/Themes/Colors';
import style from './adjust-post-paid-limit-modal.module.scss';
import { usePutPostPaid } from '@/_features/places/api/put-post-paid';
import { queryClient } from '@/_config/lib/react-query/query-client';
import { GET_PLACE_ID } from '@/_features/places/api/constants';
import { TextInput } from '@/_components/text-input/text-input';
import type { PostPaidTypesForm } from '../../entities/post-paid.types';
import { postPaidLimitSchema } from '../../entities/post-paid.constants';
import {
  convertMoneyInputFormatToFloat,
  convertStringToFloatWithTwoDecimals,
  formatCurrency,
} from '@/Utils/FnUtils';
import { Modal } from '@/_components/modal/modal';
import { useGetPlaceById } from '@/_features/places/api/get-place-by-id';

interface AdjustPostPaidLimitModalProps {
  open: boolean;
  onClose: () => void;
  placeId: string;
}

export const AdjustPostPaidLimitModal = ({
  open,
  onClose,
  placeId,
}: AdjustPostPaidLimitModalProps) => {
  const { mutate: updatePostPaid, isLoading } = usePutPostPaid();

  const { data: place } = useGetPlaceById(
    {
      placeId: placeId || '',
    },
    {
      enabled: open && !!placeId,
    },
  );

  const minLimitAllowed = place?.minLimitAllowed || 0;
  const maxLimitAllowed = place?.maxLimitAllowed || 0;
  const currentStatus = place?.postPaid?.statusPostPaid || 'ACTIVE';

  const usedValue = place?.postPaid?.usedPostPaid || 0;

  const actualMinLimitAllowed =
    usedValue > minLimitAllowed ? usedValue : minLimitAllowed;

  const { control, handleSubmit, clearErrors } = useForm<PostPaidTypesForm>({
    resolver: yupResolver(postPaidLimitSchema),
    context: {
      minAllowed: actualMinLimitAllowed,
      maxAllowed: maxLimitAllowed,
    },
  });

  const minLimitAllowedFormatted = formatCurrency(actualMinLimitAllowed);
  const maxLimitAllowedFormatted = formatCurrency(maxLimitAllowed);

  useEffect(() => {
    if (open) {
      clearErrors();
    }
  }, [open]);

  const handleSendRequest: SubmitHandler<PostPaidTypesForm> = data => {
    updatePostPaid(
      {
        placeId,
        statusPostPaid: currentStatus,
        postPaidLimit: Number(
          convertStringToFloatWithTwoDecimals(data?.postPaidLimit),
        ),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries([GET_PLACE_ID, { placeId }]);
          onClose();
        },
      },
    );
  };

  return (
    <Modal
      bodyClassName={style.bodyContainer}
      onClose={onClose}
      open={open}
      width={525}
      title="Bank - Ajustar limite de pós-pago"
    >
      <>
        <span className={style.description}>
          {`Por favor, defina o novo limite de crédito para ${place?.nomeExibicao}.`}
        </span>
        <Form className={style.form}>
          <Controller
            control={control}
            name="postPaidLimit"
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <>
                  <TextInput
                    value={value}
                    label="Limite de Crédito:"
                    labelClassName={style.inputLabel}
                    onChange={e => {
                      onChange(convertMoneyInputFormatToFloat(e.target.value));
                    }}
                    name="limit"
                    error={!!error?.message}
                    tag="R$"
                  />

                  <div className={style.creditsLimit}>
                    <span>{minLimitAllowedFormatted}</span>
                    <span>{maxLimitAllowedFormatted}</span>
                  </div>

                  {!!error?.message && (
                    <div className={style.errorMessage}>
                      <FontAwesomeIcon
                        icon={faExclamationCircle}
                        color={colors.danger50}
                      />
                      {error.message}
                    </div>
                  )}
                </>
              );
            }}
          />
        </Form>
        <div className={style.buttons}>
          <Button
            className={style.button}
            onClick={onClose}
            disabled={isLoading}
            variant="outline"
            size="lg"
          >
            cancelar
          </Button>
          <Button
            className={style.button}
            onClick={handleSubmit(handleSendRequest)}
            disabled={isLoading}
            variant="primary"
            size="lg"
          >
            salvar novo limite
          </Button>
        </div>
      </>
    </Modal>
  );
};
